/**
 * ============================================================================
 * ACCOUNT DELETION PAGE - GDPR Compliance
 * ============================================================================
 * 
 * GDPR Article 17 - Right to Erasure
 * Allows users to permanently delete their accounts
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useTransaction } from '../hooks/useTransaction';
import { useWallet } from '../hooks/useWallet';
import { useDispute } from '../hooks/useDispute';
import { useAuthContext } from '../providers';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Input from '../components/common/Input';
import Textarea from '../components/common/Textarea';
import Modal from '../components/common/Modal';
import Spinner from '../components/common/Spinner';
import StatusBadge from '../components/common/StatusBadge';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Account Deletion Page Component
 * @returns {JSX.Element}
 */
const AccountDeletion = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { deleteAccount: deleteUserAccount } = useUser();
  const { listTransactions } = useTransaction();
  const { loadWallet } = useWallet();
  const { listDisputes } = useDispute();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, language } = useI18n();

  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    password: '',
    confirmDelete: false,
  });
  const [checks, setChecks] = useState({
    pendingTransactions: 0,
    activeDisputes: 0,
    walletBalance: 0,
    canDelete: false,
  });

  useEffect(() => {
    const checkDeletionEligibility = async () => {
      try {
        setIsLoading(true);
        const [transactions, wallet, disputes] = await Promise.all([
          listTransactions({ status: ['created', 'link_sent', 'payment_pending', 'escrow_funded', 'in_progress', 'delivered', 'disputed'] }).catch(() => ({ data: [] })),
          loadWallet().catch(() => ({ balance: 0 })),
          listDisputes({ status: ['open', 'negotiating', 'mediation_requested', 'in_mediation'] }).catch(() => ({ data: [] })),
        ]);

        const pendingTx = Array.isArray(transactions) ? transactions : transactions?.data || [];
        const activeDisputesList = Array.isArray(disputes) ? disputes : disputes?.data || [];
        const balance = wallet?.balance || wallet?.availableBalance || 0;

        setChecks({
          pendingTransactions: pendingTx.length,
          activeDisputes: activeDisputesList.length,
          walletBalance: parseFloat(balance) || 0,
          canDelete: pendingTx.length === 0 && activeDisputesList.length === 0 && parseFloat(balance) === 0,
        });
      } catch (error) {
        console.error('Check eligibility error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkDeletionEligibility();
  }, [listTransactions, loadWallet, listDisputes]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
    if (!formData.confirmDelete) {
      showError(
        language === 'fr'
          ? 'Veuillez confirmer la suppression'
          : 'Please confirm deletion'
      );
      return;
    }

    if (!checks.canDelete) {
      showError(
        language === 'fr'
          ? 'Vous devez d\'abord compléter toutes les transactions en attente, résoudre tous les litiges et retirer tous les fonds.'
          : 'You must first complete all pending transactions, resolve all disputes, and withdraw all funds.'
      );
      return;
    }

    try {
      setIsDeleting(true);
      await deleteUserAccount(formData.password || undefined);
      showSuccess(
        language === 'fr'
          ? 'Votre compte sera supprimé dans 30 jours. Vous pouvez annuler cette demande en vous connectant.'
          : 'Your account will be deleted in 30 days. You can cancel this request by logging in.'
      );
      // Logout user after deletion request
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr'
            ? 'Échec de la suppression du compte'
            : 'Failed to delete account')
      );
    } finally {
      setIsDeleting(false);
      setShowConfirmModal(false);
    }
  };

  if (isLoading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings')}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour aux paramètres' : 'Back to Settings'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-red-600 via-orange-600 to-pink-600 bg-clip-text text-transparent mb-2">
          🗑️ {language === 'fr' ? 'Suppression du Compte' : 'Account Deletion'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Supprimez définitivement votre compte (GDPR Article 17)'
            : 'Permanently delete your account (GDPR Article 17)'}
        </p>
      </div>

      {/* Warning Card */}
      <Card
        variant="glass"
        className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 bg-gradient-to-br from-red-50/80 to-orange-50/80 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800 animate-fadeInUp"
        hover
      >
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-3xl shadow-lg shadow-red-500/30 animate-float">
            ⚠️
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Attention: Action Irréversible' : 'Warning: Irreversible Action'}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              {language === 'fr'
                ? 'La suppression de votre compte est permanente et irréversible. Toutes vos données personnelles seront anonymisées conformément au RGPD. Les enregistrements financiers seront conservés pendant 7 ans pour des raisons légales.'
                : 'Account deletion is permanent and irreversible. All your personal data will be anonymized in accordance with GDPR. Financial records will be retained for 7 years for legal reasons.'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'fr'
                ? 'Article 17 du RGPD - Droit à l\'effacement'
                : 'GDPR Article 17 - Right to Erasure'}
            </p>
          </div>
        </div>
      </Card>

      {/* Eligibility Checks */}
      <Card
        variant="glass"
        className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
        style={{ animationDelay: '0.1s' }}
        hover
      >
        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">
          {language === 'fr' ? 'Vérifications Préalables' : 'Pre-Deletion Checks'}
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📦</div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">
                  {language === 'fr' ? 'Transactions en Attente' : 'Pending Transactions'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr'
                    ? 'Vous devez compléter ou annuler toutes les transactions en cours'
                    : 'You must complete or cancel all ongoing transactions'}
                </p>
              </div>
            </div>
            <StatusBadge
              variant={checks.pendingTransactions === 0 ? 'success' : 'error'}
              size="lg"
            >
              {checks.pendingTransactions === 0
                ? language === 'fr' ? '✓ Aucune' : '✓ None'
                : `${checks.pendingTransactions} ${language === 'fr' ? 'en attente' : 'pending'}`}
            </StatusBadge>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3">
              <div className="text-2xl">⚠️</div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">
                  {language === 'fr' ? 'Litiges Actifs' : 'Active Disputes'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr'
                    ? 'Vous devez résoudre tous les litiges en cours'
                    : 'You must resolve all ongoing disputes'}
                </p>
              </div>
            </div>
            <StatusBadge
              variant={checks.activeDisputes === 0 ? 'success' : 'error'}
              size="lg"
            >
              {checks.activeDisputes === 0
                ? language === 'fr' ? '✓ Aucun' : '✓ None'
                : `${checks.activeDisputes} ${language === 'fr' ? 'actifs' : 'active'}`}
            </StatusBadge>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3">
              <div className="text-2xl">💰</div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">
                  {language === 'fr' ? 'Solde du Portefeuille' : 'Wallet Balance'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr'
                    ? 'Vous devez retirer tous les fonds avant la suppression'
                    : 'You must withdraw all funds before deletion'}
                </p>
              </div>
            </div>
            <StatusBadge
              variant={checks.walletBalance === 0 ? 'success' : 'error'}
              size="lg"
            >
              {checks.walletBalance === 0
                ? language === 'fr' ? '✓ Vide' : '✓ Empty'
                : formatCurrency(checks.walletBalance, 'XAF')}
            </StatusBadge>
          </div>
        </div>

        {!checks.canDelete && (
          <div className="mt-6 p-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
              {language === 'fr'
                ? '⚠️ Vous ne pouvez pas supprimer votre compte tant que les vérifications ci-dessus ne sont pas complètes.'
                : '⚠️ You cannot delete your account until the above checks are complete.'}
            </p>
          </div>
        )}
      </Card>

      {/* What Happens After Deletion */}
      <Card
        variant="glass"
        className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
        style={{ animationDelay: '0.2s' }}
        hover
      >
        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-4">
          {language === 'fr' ? 'Ce qui se passe après la suppression' : 'What Happens After Deletion'}
        </h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-red-500 mt-1">•</span>
            <span className="text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? 'Vos données personnelles seront anonymisées (nom, email, téléphone, photo)'
                : 'Your personal data will be anonymized (name, email, phone, photo)'}
            </span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-red-500 mt-1">•</span>
            <span className="text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? 'Vous ne pourrez plus vous connecter à votre compte'
                : 'You will no longer be able to log in to your account'}
            </span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-red-500 mt-1">•</span>
            <span className="text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? 'Les enregistrements financiers seront conservés pendant 7 ans (obligation légale)'
                : 'Financial records will be retained for 7 years (legal requirement)'}
            </span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-red-500 mt-1">•</span>
            <span className="text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? 'Vous avez 30 jours pour annuler la demande de suppression'
                : 'You have 30 days to cancel the deletion request'}
            </span>
          </div>
        </div>
      </Card>

      {/* Deletion Form */}
      {checks.canDelete && (
        <Card
          variant="glass"
          className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
          style={{ animationDelay: '0.3s' }}
          hover
        >
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">
            {language === 'fr' ? 'Demande de Suppression' : 'Deletion Request'}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                {language === 'fr' ? 'Raison (optionnelle)' : 'Reason (Optional)'}
              </label>
              <Textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                placeholder={
                  language === 'fr'
                    ? 'Dites-nous pourquoi vous supprimez votre compte...'
                    : 'Tell us why you\'re deleting your account...'
                }
                rows={4}
              />
            </div>

            {user?.hasPassword && (
              <div>
                <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                  {language === 'fr' ? 'Mot de passe' : 'Password'}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={
                    language === 'fr'
                      ? 'Entrez votre mot de passe pour confirmer'
                      : 'Enter your password to confirm'
                  }
                />
              </div>
            )}

            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800">
              <input
                type="checkbox"
                id="confirmDelete"
                checked={formData.confirmDelete}
                onChange={(e) => handleInputChange('confirmDelete', e.target.checked)}
                className="mt-1 w-5 h-5 text-red-600 border-red-300 rounded focus:ring-red-500"
              />
              <label htmlFor="confirmDelete" className="text-sm font-medium text-red-800 dark:text-red-200">
                {language === 'fr'
                  ? 'Je comprends que cette action est irréversible et je confirme que je souhaite supprimer mon compte.'
                  : 'I understand that this action is irreversible and I confirm that I want to delete my account.'}
              </label>
            </div>

            <Button
              variant="gradient"
              size="lg"
              fullWidth
              onClick={() => setShowConfirmModal(true)}
              disabled={!formData.confirmDelete || isDeleting}
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 shadow-xl hover:shadow-2xl transition-all"
            >
              {language === 'fr' ? 'Demander la Suppression' : 'Request Deletion'}
            </Button>
          </div>
        </Card>
      )}

      {/* Alternative Actions */}
      <Card
        variant="glass"
        className="backdrop-blur-xl border-white/20 dark:border-white/10 animate-fadeInUp"
        style={{ animationDelay: '0.4s' }}
        hover
      >
        <h2 className="text-xl font-black text-gray-900 dark:text-white mb-4">
          {language === 'fr' ? 'Alternatives à la Suppression' : 'Alternatives to Deletion'}
        </h2>
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {language === 'fr'
              ? 'Avant de supprimer votre compte, considérez ces alternatives:'
              : 'Before deleting your account, consider these alternatives:'}
          </p>
          <Button
            variant="outline"
            fullWidth
            onClick={() => navigate('/settings')}
            className="flex items-center justify-center gap-2"
          >
            <span>⚙️</span>
            <span>{language === 'fr' ? 'Modifier les paramètres' : 'Adjust Settings'}</span>
          </Button>
          <Button
            variant="outline"
            fullWidth
            onClick={() => navigate('/settings/privacy')}
            className="flex items-center justify-center gap-2"
          >
            <span>🔒</span>
            <span>{language === 'fr' ? 'Modifier la confidentialité' : 'Change Privacy Settings'}</span>
          </Button>
          <Button
            variant="outline"
            fullWidth
            onClick={() => navigate('/contact')}
            className="flex items-center justify-center gap-2"
          >
            <span>💬</span>
            <span>{language === 'fr' ? 'Contacter le support' : 'Contact Support'}</span>
          </Button>
        </div>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={language === 'fr' ? 'Confirmer la Suppression' : 'Confirm Deletion'}
      >
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-200 font-medium">
              {language === 'fr'
                ? '⚠️ Cette action est permanente et irréversible. Êtes-vous absolument sûr?'
                : '⚠️ This action is permanent and irreversible. Are you absolutely sure?'}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setShowConfirmModal(false)}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={handleDelete}
              loading={isDeleting}
              disabled={isDeleting}
              className="bg-gradient-to-r from-red-600 to-orange-600"
            >
              {isDeleting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {language === 'fr' ? 'Suppression...' : 'Deleting...'}
                </>
              ) : (
                language === 'fr' ? 'Confirmer la Suppression' : 'Confirm Deletion'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </UserDashboardLayout>
  );
};

export default AccountDeletion;
